import React, { FC, useEffect, useState } from 'react';
import SVGInline from 'components/SVGInline/SVGInline';
import { DetailPageScreenName } from 'types/Tracking';
import { DetailEpisode, Episode, TVSeriesDetailEpisode } from 'types/Asset';
import translate from 'utils/fnTranslate';
import { Lane } from 'components/Lane/Lane';
import { ArrowIcon } from 'components/Lane/Lane.css';
import icons from 'style';
import { DropdownItemSimple, DropdownMenu } from 'components/DropdownMenu/DropdownMenu';
import { Slider } from 'components/Slider/Slider';
import { isTvSeriesEpisode, isVODSeriesEpisode } from 'utils/fnTypeGuards';
import { isNow } from 'utils/fnDate';
import { useTVAssetEntitlementState } from 'hooks/useTVAssetEntitlementState/useTVAssetEntitlementState';
import { SubscribeData, TVAssetEntitlementState } from 'types/Entitlement';
import { AssetTypes } from 'types/Common';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { getCatchupPlayerURL, getLivePlayerURL } from 'utils/fnUrl';
import { useSubscription } from 'providers/useSubscription/SubscriptionContext';
import { useUpsellFlow } from 'providers/useUpsellFlow/UpsellFlowContext';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useVodWatchable } from 'hooks/useVodWatchable';
import {
    EpisodesWrapper,
    SeasonNumber,
    SeasonSelectorDropdownContainer,
    SeasonSelectorDropdownTrigger,
    SeasonSelectorWithDropDown,
    SeasonSelectorWithLaneWrapper,
    SeasonSelectorWrapper,
    SeasonsLaneWrapper,
    SeasonsSectionLabel,
    SeasonsWrapper,
} from '../PageSeriesDetail.css';

const SeriesDetailsEpisodeSelector: FC<{
    type: 'TV' | 'VOD';
    screenName: DetailPageScreenName;
    episodeList;
    episode: DetailEpisode | TVSeriesDetailEpisode;
    selected: number;
    initialLaneScrollEnabled: boolean;
    resetEpisodeListScroll: boolean;
    onEpisodeSelected: (episodeId: string) => void;
    onSeasonSelected: (seasonNumber: number) => void;
    trailerEpisodes: (DetailEpisode | TVSeriesDetailEpisode)[];
    hideChannelLogo?: boolean;
    hideGenre?: boolean;
}> = ({
    type,
    screenName,
    episode,
    episodeList,
    selected,
    resetEpisodeListScroll,
    initialLaneScrollEnabled,
    onSeasonSelected,
    trailerEpisodes,
    hideChannelLogo,
    hideGenre,
}) => {
    const { isGuest } = useAuth();
    const { config } = useConfig();
    const { initSubscribeFlow } = useSubscription();
    const { initUpsellFlow } = useUpsellFlow();
    const { hasVodAssetPurchase, isVodEpisodeWatchable } = useVodWatchable(!isGuest);
    const getEntitlementState = useTVAssetEntitlementState();

    const { product_code_time_machine_plus } = config?.app_config?.product_settings ?? {};

    const selectedSeasonIndex = episode?.season_number;
    const selectedChannelId = type === 'TV' ? (episode as TVSeriesDetailEpisode)?.listings?.[0]?.channelId : null;

    const [episodes, setEpisodes] = useState<(DetailEpisode | TVSeriesDetailEpisode)[]>([]);
    const [isSeasonDropdownOpen, setIsSeasonDropdownOpen] = useState(false);

    const selectedEpisodeIndex = episodes.findIndex((ep) => ep.id === episode?.id);

    // workaround to get the season list, because theres no better way to get it
    const seasonList = episodeList.reduce((unique, ep) => {
        if (!ep || ep.season_number === null) return unique;
        if (unique.includes(ep.season_number)) return unique;
        if (selectedChannelId && (ep as TVSeriesDetailEpisode)?.listings?.[0]?.channelId !== selectedChannelId) return unique;
        return [...unique, ep.season_number];
    }, []);

    const onCardEpisodeClick = (event, asset) => {
        if (!isGuest && isTvSeriesEpisode(asset)) {
            const entitlementState = getEntitlementState(asset);

            let subscribeData: SubscribeData;
            switch (entitlementState) {
                case TVAssetEntitlementState.SHOULD_BUY_BASIC_REPLAY_WINDOW:
                case TVAssetEntitlementState.SHOULD_BUY_EXTENDED_REPLAY_WINDOW:
                    subscribeData = {
                        type: 'Product',
                        code: product_code_time_machine_plus,
                    };

                    break;
                case TVAssetEntitlementState.SHOULD_SUBSCRIBE_CHANNEL:
                    subscribeData = {
                        type: 'Channel',
                        code: asset?.subscribeOffer?.productCode,
                    };

                    break;
                default: {
                    break;
                }
            }

            if (subscribeData) {
                event?.preventDefault();
                event?.stopPropagation();

                const path = isNow(asset.startTime, asset.endTime) ? getLivePlayerURL(asset.channelId) : getCatchupPlayerURL(asset.eventId);

                initSubscribeFlow(path, subscribeData);
            }
        }

        if (!isGuest && isVODSeriesEpisode(asset)) {
            const alreadyOwned = isVodEpisodeWatchable(asset) || hasVodAssetPurchase(asset?.id);
            const hasOfferToShow =
                asset?.vod_terms?.some((term) => term.type === AssetTypes.tvod) || !!asset?.additional_services_available;

            if (alreadyOwned || !hasOfferToShow) {
                return;
            }

            event?.preventDefault();
            event?.stopPropagation();

            initUpsellFlow(null, asset as unknown as Episode);
        }
    };

    useEffect(() => {
        setEpisodes(
            episodeList.filter(
                (ep) =>
                    ep.season_number === selected &&
                    (!selectedChannelId || (ep as TVSeriesDetailEpisode)?.listings?.[0]?.channelId === selectedChannelId)
            )
        );
    }, [episodeList, selected, episode, type]);

    useEffect(() => {
        if (episodeList.length > 0) {
            onSeasonSelected(selectedSeasonIndex);
        }
    }, []);

    return (
        <SeasonSelectorWrapper>
            <SeasonsWrapper>
                <SeasonSelectorWithLaneWrapper>
                    {episodeList?.length > 0 && (
                        <SeasonsSectionLabel data-test-id={'tv-detailscreen-seasonslabel'}>
                            {episodeList?.length > 1 ? translate('NUMBER_SEASONS') : translate('NUMBER_SEASON')}
                        </SeasonsSectionLabel>
                    )}
                    <SeasonsLaneWrapper>
                        <Lane
                            arrowLeft={<ArrowIcon svg={icons.leftArrowIcon} />}
                            arrowRight={<ArrowIcon svg={icons.rightArrowIcon} />}
                            scrollable={true}
                            keepArrows={false}
                            showArrowsWhenActive={true}
                            withoutPadding={true}
                            stripeType="seasons"
                            navigateTo={
                                selected && initialLaneScrollEnabled
                                    ? {
                                          number: seasonList.indexOf(selected),
                                          smooth: false,
                                      }
                                    : null
                            }
                        >
                            {seasonList.map((season, index) => (
                                <SeasonNumber
                                    data-test-id={'tv-detailscreen-seasonsnumber'}
                                    key={`season-${season}-${index}`}
                                    type="button"
                                    selected={season === selected}
                                    aria-pressed={season === selected}
                                    aria-label={
                                        season === -1 || Number.isNaN(season) ? undefined : `${translate('NUMBER_SEASON')} ${season}`
                                    }
                                    onClick={() => onSeasonSelected(season)}
                                >
                                    {season === -1 || Number.isNaN(season) ? translate('SEASON_SELECTOR_OTHER_EPISODES') : season}
                                </SeasonNumber>
                            ))}
                            {trailerEpisodes?.length > 0 && (
                                <SeasonNumber
                                    data-test-id={'tv-detailscreen-trailer'}
                                    key={`trailers-and-more`}
                                    type="button"
                                    selected={selected === 0}
                                    aria-pressed={selected === 0}
                                    onClick={() => onSeasonSelected(0)}
                                >
                                    {translate('SEASON_TRAILERS_AND_MORE')}
                                </SeasonNumber>
                            )}
                        </Lane>
                    </SeasonsLaneWrapper>
                </SeasonSelectorWithLaneWrapper>
                <SeasonSelectorWithDropDown>
                    <DropdownMenu
                        triggerButton={
                            <SeasonSelectorDropdownTrigger type="button" aria-expanded={isSeasonDropdownOpen}>
                                <SeasonSelectorDropdownContainer>
                                    {selected > 0
                                        ? `${translate('NUMBER_SEASON')} ${selected}`
                                        : selected === 0
                                          ? translate('SEASON_TRAILERS_AND_MORE')
                                          : translate('SEASON_SELECTOR_OTHER_EPISODES')}

                                    <SVGInline svg={icons.bottomArrowSmallIcon} />
                                </SeasonSelectorDropdownContainer>
                            </SeasonSelectorDropdownTrigger>
                        }
                        horizontalPosition={'right'}
                        selectedIndex={selectedSeasonIndex}
                        visibilityChangeCallback={setIsSeasonDropdownOpen}
                    >
                        {seasonList.map((season, index) => (
                            <DropdownItemSimple
                                key={`season-${season}-${index}`}
                                selected={season === selected}
                                onClick={() => onSeasonSelected(season)}
                            >
                                {season > 0
                                    ? `${translate('NUMBER_SEASON')} ${season}`
                                    : season === 0
                                      ? translate('SEASON_TRAILERS_AND_MORE')
                                      : translate('SEASON_SELECTOR_OTHER_EPISODES')}
                            </DropdownItemSimple>
                        ))}
                        {trailerEpisodes?.length > 0 && (
                            <DropdownItemSimple key={`trailers-and-more`} selected={selected === 0} onClick={() => onSeasonSelected(0)}>
                                {translate('SEASON_TRAILERS_AND_MORE')}
                            </DropdownItemSimple>
                        )}
                    </DropdownMenu>
                </SeasonSelectorWithDropDown>
            </SeasonsWrapper>
            <EpisodesWrapper type={type}>
                <Slider
                    title={translate('SCREEN_DETAIL_SEASON_TAB_SELECTOR')}
                    assets={selected === 0 ? (trailerEpisodes ?? []) : (episodes ?? [])}
                    isLoading={false}
                    stripePosition={0}
                    screenName={screenName}
                    onCardClick={onCardEpisodeClick}
                    disableUiStoreState={true}
                    resetScroll={resetEpisodeListScroll}
                    initScrollTo={initialLaneScrollEnabled && selectedEpisodeIndex >= 0 ? selectedEpisodeIndex : null}
                    hideChannelLogo={hideChannelLogo}
                    showEpisodeDescription={type === 'TV' || type === 'VOD'}
                    hideGenre={hideGenre}
                />
            </EpisodesWrapper>
        </SeasonSelectorWrapper>
    );
};

export default SeriesDetailsEpisodeSelector;
